// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (/admin/claims and /admin/requests — the two review queues)
//  · (Complaints moved to pages/admin/AdminRefunds — the refund & claims console.)
//  · Data requests: erasure requests. Staff act on them OUTSIDE this screen (nothing is deleted automatically), then mark them completed or
//    declined with a note the customer sees.
// Viewers see the queues read-only. Both call the staff endpoints the backend already enforces (editor role for decisions).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { CircleAlert, Loader2 } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { ApiError } from "../../utils/api";
import { decideErasure, fetchErasureQueue, type ErasureItem } from "../../utils/adminApi";
import { formatWhen } from "../../utils/orders";

const card = "rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10";
const field = "mt-1 w-full rounded-xl bg-white px-3 text-sm text-ocean-950 ring-1 ring-ocean-900/20 focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-ocean-950 dark:text-foam-50";

function useQueue<T>(load: () => Promise<T[]>) {
  const [rows, setRows] = useState<T[] | null>(null);
  const [failed, setFailed] = useState(false);
  const reload = () => load().then((r) => { setRows(r); setFailed(false); }, () => setFailed(true));
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { rows, failed, reload };
}

const Status = ({ failed, rows, label }: { failed: boolean; rows: unknown[] | null; label: string }) => (
  <>
    {failed && <p role="alert" className="rounded-xl bg-coral-500/10 p-4 font-semibold">We couldn&apos;t load {label}.</p>}
    {!rows && !failed && <p role="status" className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading…</p>}
    {rows && rows.length === 0 && <p className="rounded-2xl bg-white p-6 text-center shadow-sm dark:bg-ocean-900">Nothing waiting — {label} are all decided.</p>}
  </>
);

export function AdminRequests() {
  const canWrite = ["admin", "editor"].includes(useAuth().user?.role ?? "");
  const { rows, failed, reload } = useQueue<ErasureItem>(fetchErasureQueue);
  const [note, setNote] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const decide = async (item: ErasureItem, decision: "completed" | "declined") => {
    setBusy(item.uid);
    setError(null);
    try {
      await decideErasure(item.uid, decision, note[item.uid] ?? "");
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "We couldn't save that decision.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="font-display text-2xl font-bold">Data erasure requests</h1>
      <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">Nothing is deleted automatically. Do the erasure or anonymisation yourself first (keeping records the law requires — Privacy Policy §8), then mark the request here.</p>
      <p role="alert" className="flex min-h-5 items-center gap-1.5 text-sm text-coral-600 dark:text-coral-300">{error && <CircleAlert className="h-4 w-4" aria-hidden="true" />}{error}</p>
      <Status failed={failed} rows={rows} label="requests" />
      <ul className="flex flex-col gap-3">
        {rows?.map((item) => (
          <li key={item.uid} className={card} data-testid="admin-request">
            <p className="break-all font-bold">{item.uid}</p>
            <p className="text-xs text-ocean-800/75 dark:text-foam-100/70">Requested {formatWhen(item.createdAt)}</p>
            {item.note && <p className="mt-1 rounded-lg bg-ocean-700/5 p-2 text-sm dark:bg-white/5">“{item.note}”</p>}
            {canWrite && (
              <div className="mt-3">
                <label htmlFor={`r-${item.uid}`} className="text-xs font-bold">Note to the customer (optional)</label>
                <input id={`r-${item.uid}`} maxLength={300} value={note[item.uid] ?? ""} onChange={(e) => setNote({ ...note, [item.uid]: e.target.value })} className={`${field} min-h-11`} />
                <div className="mt-2 flex gap-2">
                  <button type="button" disabled={busy === item.uid} onClick={() => void decide(item, "completed")} className="min-h-11 rounded-full bg-teal-700 px-5 text-sm font-bold text-white disabled:opacity-60">Mark completed</button>
                  <button type="button" disabled={busy === item.uid} onClick={() => void decide(item, "declined")} className="min-h-11 rounded-full bg-coral-500/10 px-5 text-sm font-bold text-coral-600 disabled:opacity-60 dark:text-coral-300">Decline</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
